import React, { useState, useCallback, useRef } from 'react';
import { Repack, PackType, Pack, Sample } from 'types/generated/graphql';
import useMountEffect from 'hooks/useMountEffect';
import { getArtworkLeftAndWidth } from '../helpers/getArtworkLeftAndWidth';
import { PreviewContextContent, PreviewContextSamples } from '../types';
import { Spacer, FallbackWidth } from '../constant';
import usePackQuery from './usePackQuery';
import useRepackQuery from './useRepackQuery';

const usePreviewProvider = ({
    isPreviewAllowed,
}: {
    isPreviewAllowed: boolean;
}) => {
    const parentRef = useRef<HTMLDivElement | null>(null);
    const [isPreviewVisible, setIsPreviewVisible] = useState(false);
    const [preview, setPreview] = useState<PreviewContextContent | undefined>(
        undefined,
    );
    const [samples, setSamples] = useState<PreviewContextSamples | undefined>(
        undefined,
    );
    const [artworkLeft, setArtworkLeft] = useState<number>(Spacer);
    const [artworkWidth, setArtworkWidth] = useState<number>(FallbackWidth);

    const updatePreview = useCallback((entity: Pack | Repack) => {
        const genres = [
            (entity as Pack | Repack)?.mainGenre,
            ...((entity as Pack | Repack)?.genres ?? []),
        ].filter((genre) => genre !== undefined);

        const newEntityPreview = {
            entity: entity,
            guid: entity?.guid,
            slug: entity?.slug ?? undefined,
            title: entity?.name ?? undefined,
            description: entity?.description ?? undefined,
            labelName: entity?.label?.name ?? undefined,
            backgroundColor: entity?.backgroundColor ?? undefined,
            attributes: { genres },
            type: entity?.type,
            artworkUrl: entity?.artworkUrl ?? undefined,
        };

        setPreview(newEntityPreview);
    }, []);

    const updateSamples = useCallback(
        ({ items, total }: { items: Sample[]; total: number }) => {
            const newSamplesPreview = {
                items: items,
                total: total,
            };

            setSamples(newSamplesPreview);
        },
        [],
    );

    const getPackPreview = usePackQuery({
        updatePreview,
        updateSamples,
    });
    const getRepackPreview = useRepackQuery({
        updatePreview,
        updateSamples,
    });

    const getPreviewContent = useCallback(
        (guid: string, type: PackType) => {
            switch (type) {
                case PackType.Pack:
                    getPackPreview(guid);
                    break;

                case PackType.Repack:
                    getRepackPreview(guid);
                    break;
            }
        },
        [getPackPreview, getRepackPreview],
    );

    const hidePreview = useCallback(() => {
        setIsPreviewVisible(false);
        setArtworkLeft(Spacer);
        setArtworkWidth(FallbackWidth);
        setPreview(undefined);
        setSamples(undefined);
    }, []);

    useMountEffect(
        () => {
            window.addEventListener('resize', hidePreview);
        },
        () => window.removeEventListener('resize', hidePreview),
    );

    const showPreview = useCallback(
        ({
            guid,
            type,
            slideRef,
        }: {
            guid: string;
            type: PackType;
            slideRef: React.MutableRefObject<HTMLDivElement | null>;
        }) => {
            if (isPreviewAllowed) {
                getPreviewContent(guid, type);

                const slideRect = slideRef?.current?.getBoundingClientRect();
                const parentRect = parentRef?.current?.getBoundingClientRect();
                const { left, width } = getArtworkLeftAndWidth(
                    parentRect,
                    slideRect,
                );

                setArtworkLeft(left);
                setArtworkWidth(width);
                setIsPreviewVisible(true);
            }
        },
        [getPreviewContent, isPreviewAllowed],
    );

    return {
        preview,
        samples,
        artworkLeft,
        artworkWidth,
        isPreviewVisible,
        showPreview,
        hidePreview,
        parentRef,
    };
};

export default usePreviewProvider;
